import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, storage, auth } from '../../firebase/config';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaImage, FaFileUpload } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const AddButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  @media (min-width: 576px) {
    width: auto;
  }
  
  &:hover {
    opacity: 0.9;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (min-width: 576px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  
  @media (min-width: 576px) {
    width: auto;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
  text-align: center;
  
  @media (min-width: 576px) {
    width: auto;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }
`;

const PlanCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1.25rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const PlanTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.1rem;
  
  @media (min-width: 768px) {
    font-size: 1.3rem;
  }
`;

const PlanCategory = styled.span`
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  text-transform: capitalize;
`;

const PlanPrice = styled.div`
  font-size: 1.3rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const PlanDescription = styled.p`
  color: #666;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: ${({ $variant }) => 
    $variant === 'danger' ? '#dc3545' : 
    $variant === 'warning' ? '#ffc107' : '#6c757d'};
  color: ${({ $variant }) => $variant === 'warning' ? '#000' : 'white'};
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageUploadArea = styled.div`
  border: 2px dashed #ddd;
  border-radius: 4px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  margin-top: 1rem;
  border-radius: 4px;
  object-fit: cover;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #eee;
  border-radius: 4px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.primary};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

const AdminPlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'diet',
    duration: '',
    features: '',
    imageUrl: '',
    preference: '1',
    featured: false
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'plans'));
      const plansList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlans(plansList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError("Image file size must be less than 5MB");
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, or WEBP)");
        return;
      }
      
      setImageFile(file);
      setError(null);
      setUploadProgress(0);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({ ...prev, previewImage: e.target.result }));
      };
      reader.onerror = () => {
        setError("Failed to create image preview");
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToStorage = async (file) => {
    if (!file) return null;
    
    try {
      // Use the Cloud Function for image upload instead of direct Storage access
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'plans');
      
      // Add user ID if available
      if (auth.currentUser) {
        formData.append('userId', auth.currentUser.uid);
      }
      
      // Show upload starting
      console.log(`Starting upload for file: ${file.name}`);
      setUploadProgress(10); // Set initial progress
      
      // Send file to Cloud Function with timeout and error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
      
      try {
        // Send file to Cloud Function
        const response = await fetch('https://uploadimage-jzyeayelrq-uc.a.run.app', {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId); // Clear the timeout
        
        // Progress to 90% when response is received
        setUploadProgress(90);
        
        // Check if response is OK
        if (!response.ok) {
          // Try to get error details from response
          let errorDetails = 'Unknown error';
          try {
            const errorData = await response.json();
            errorDetails = errorData.error || `Server error: ${response.status}`;
          } catch (e) {
            errorDetails = `HTTP error: ${response.status}`;
          }
          
          throw new Error(errorDetails);
        }
        
        // Get the URL from the response
        const data = await response.json();
        setUploadProgress(100); // Set to complete
        
        // Short delay before resetting progress
        setTimeout(() => setUploadProgress(0), 1000);
        
        console.log('Upload successful:', data.url);
        return data.url;
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload timed out after 60 seconds. Please try again with a smaller image.');
        }
        throw fetchError;
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Upload failed: ${err.message}`);
      setLoading(false);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Handle image upload first if there's a new image
      let imageUrl = formData.imageUrl;
      
      if (imageFile) {
        const uploadedImageUrl = await uploadImageToStorage(imageFile);
        if (uploadedImageUrl) {
          imageUrl = uploadedImageUrl;
        } else {
          // If image upload failed but it's optional, we can still proceed
          if (!formData.imageUrl) {
            setError('Image upload failed. Please try again or proceed without an image.');
          }
        }
      }

      const planData = {
        ...formData,
        imageUrl: imageUrl,
        price: parseFloat(formData.price),
        duration: parseInt(formData.duration),
        preference: parseInt(formData.preference),
        featured: Boolean(formData.featured),
        features: formData.features.split('\n').filter(f => f.trim()),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Remove the preview image from data before saving to Firestore
      delete planData.previewImage;

      if (editingPlan) {
        await updateDoc(doc(db, 'plans', editingPlan.id), planData);
      } else {
        await addDoc(collection(db, 'plans'), planData);
      }

      await fetchPlans();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving plan:', error);
      setError(`Failed to save plan: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price.toString(),
      category: plan.category,
      duration: plan.duration.toString(),
      features: plan.features.join('\n'),
      imageUrl: plan.imageUrl || '',
      previewImage: plan.imageUrl || '',
      preference: plan.preference?.toString() || '1',
      featured: plan.featured || false
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await deleteDoc(doc(db, 'plans', planId));
        await fetchPlans();
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setImageFile(null);
    setUploadProgress(0);
    setError(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'diet',
      duration: '',
      features: '',
      imageUrl: '',
      preference: '1',
      featured: false
    });
  };

  if (loading && plans.length === 0) {
    return (
      <Container>
        <LoadingMessage>Loading plans...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Plan Management</Title>
        <AddButton onClick={() => setShowModal(true)}>
          <FaPlus />
          Add New Plan
        </AddButton>
      </Header>

      <PlansGrid>
        {plans.map(plan => (
          <PlanCard key={plan.id}>
            <PlanHeader>
              <div>
                <PlanTitle>{plan.name}</PlanTitle>
                <PlanCategory>{plan.category}</PlanCategory>
              </div>
              <ActionButtons>
                <IconButton $variant="warning" onClick={() => handleEdit(plan)}>
                  <FaEdit />
                </IconButton>
                <IconButton $variant="danger" onClick={() => handleDelete(plan.id)}>
                  <FaTrash />
                </IconButton>
              </ActionButtons>
            </PlanHeader>
            
            {plan.imageUrl && (
              <div style={{ marginBottom: '1rem' }}>
                <ImagePreview src={plan.imageUrl} alt={plan.name} />
              </div>
            )}
            
            <PlanPrice>${plan.price}</PlanPrice>
            <PlanDescription>{plan.description}</PlanDescription>
            
            <div>
              <strong>Duration:</strong> {plan.duration} days
            </div>
          </PlanCard>
        ))}
      </PlansGrid>

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <h2>{editingPlan ? 'Edit Plan' : 'Add New Plan'}</h2>
              <CloseButton onClick={handleCloseModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            {error && (
              <div style={{ color: '#d9534f', marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
                {error}
              </div>
            )}

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Plan Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="category">Category</Label>
                <Select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="diet">Diet Plan</option>
                  <option value="workout">Workout Plan</option>
                  <option value="bundle">Bundle</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="price">Price ($)</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="preference">Display Order</Label>
                <Select
                  id="preference"
                  name="preference"
                  value={formData.preference}
                  onChange={handleInputChange}
                >
                  <option value="1">First</option>
                  <option value="2">Second</option>
                  <option value="3">Third</option>
                  <option value="4">Fourth</option>
                  <option value="5">Fifth</option>
                  <option value="6">Sixth</option>
                  <option value="7">Seventh</option>
                  <option value="8">Eighth</option>
                  <option value="9">Ninth</option>
                  <option value="10">Tenth</option>
                </Select>
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  Select the order in which this plan should appear
                </small>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="duration">Duration (days)</Label>
                <Input
                  id="duration"
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="description">Description</Label>
                <TextArea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="features">Features (one per line)</Label>
                <TextArea
                  id="features"
                  name="features"
                  value={formData.features}
                  onChange={handleInputChange}
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                  />{' '}
                  Feature this plan on homepage
                </Label>
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  Check this box to display this plan on the homepage
                </small>
              </FormGroup>

              <FormGroup>
                <Label>Plan Image</Label>
                <input 
                  type="file" 
                  id="image" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image">
                  <ImageUploadArea>
                    <FaImage size={40} color="#757575" />
                    <p style={{ margin: '1rem 0' }}>
                      {imageFile ? imageFile.name : 'Drag and drop an image or click to browse'}
                    </p>
                    <div style={{ display: 'inline-block', padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', borderRadius: '4px' }}>
                      <FaFileUpload style={{ marginRight: '0.5rem' }} /> 
                      Choose Image
                    </div>
                  </ImageUploadArea>
                </label>
                
                {uploadProgress > 0 && (
                  <ProgressBar>
                    <ProgressFill progress={uploadProgress} />
                  </ProgressBar>
                )}
                
                {formData.previewImage && (
                  <ImagePreview src={formData.previewImage} alt="Plan preview" />
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="imageUrl">Image URL (optional or will be filled automatically)</Label>
                <Input
                  id="imageUrl"
                  name="imageUrl"
                  type="url"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                  placeholder="Or enter an image URL directly"
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  <FaSave />
                  {loading ? 'Saving...' : 'Save Plan'}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminPlanManagement;